import Button from "./Button";
import PointerEvents from "../../../src/const/PointerEvents";
import Layout from "../../utils/Layout";
import { Sprite, utils as PixiUtils, Graphics, BitmapText, Text, Point } from "pixi.js";
import EventEmitter = PixiUtils.EventEmitter;
import TextField from "./TextField";
import SoundController from "../../utils/SoundController";
import App from "../../App";
import GameModel from "../../model/GameModel";
import GameEvents from "../../const/GameEvents";
import ConfigManager from "../../managers/ConfigManager";
import { ConfigurationTypes } from "../../const/ConfigurationTypes";

export default class Slider extends EventEmitter {
    public minValue: number = 0;
    public maxValue: number = 100;
    protected handle: Button;
    protected upBtn: Button;
    protected downBtn: Button;
    protected track: Sprite;
    protected hitArea: Graphics;
    protected maxText: Text | BitmapText | TextField;
    protected minText: Text | BitmapText | TextField;
    protected fill: Sprite;
    protected fillMask: Graphics;
    protected down: boolean = false;
    protected hitDown: boolean = false;
    protected reverse: boolean = false;
    protected value: string;
    protected handleSprite: Sprite;
    protected bounds: { start: number, end: number };
    protected distance: number;
    protected upSound: string;
    protected orientation: SliderOrientation;
    protected wheelRatio: number = 1 / 10;
    protected contentSlideSpeedModifier: number = 1 / 5;
    protected hitDownPosition: Point;
    protected hitAreaTolerance: number = GameModel.isMobile() ? 0 : 10;
    protected isDestroyed: boolean = false;
    private wheelFunc: any;
    private frameOffset: number = 0;
    private _id: string = "";

    constructor(elements: ISliderElements, orientation?: SliderOrientation, reverse = false, soundName?, id?: string) {
        super();
        for (let name in elements) {
            this[name] = elements[name];
        }
        if (id) {
            this._id = id;
        }

        this.upSound = soundName;
        this.orientation = orientation || SliderOrientation.Horizontal;
        this.handleSprite = this.handle.children[0] as Sprite;
        this.reverse = reverse;
        this.setBounds();

        App.dispatcher.on(
            GameEvents.RESIZE,
            (orientationChanged: boolean) => {
                if (orientationChanged) {
                    this.onOrientationChanged();
                }
            },
            this);

        this.createFill();
        this.update();
        this.setUpListeners();
        this.setFrameOffset();
    }

    public get id() {
        return this._id;
    }

    public setFrameOffset() {
        let value = ConfigManager.getConfig(ConfigurationTypes.COMPONENTS)?.sliders?.find(s => s.id === this.id)?.frameOffset;
        this.frameOffset = Layout.scalePixels(value || 0) ;
    }

    public destroy() {
        if (this.isDestroyed) {
            return;
        }

        this.isDestroyed = true;
        this.handle.off(PointerEvents.MOVE, this.onMove, this);

        this.handle.off(PointerEvents.DOWN, this.onDown, this);

        this.track.interactive = false;

        this.track.off(PointerEvents.DOWN, this.onTrackDown, this);
        if (this.hitArea) {
            this.hitArea.interactive = false;
            this.hitArea.off(PointerEvents.DOWN, this.onHitDown, this);
            this.hitArea.off(PointerEvents.MOVE, this.onHitAreaMove, this)
        }

        App.instance.view.removeEventListener("wheel", this.wheelFunc, false);

        if (this.upBtn) {
            this.upBtn.off(PointerEvents.DOWN, this.onClickUpBtn)
        }

        if (this.downBtn) {
            this.downBtn.off(PointerEvents.DOWN, this.onClickDownBtn)
        }

        this.removeAllListeners();
    }

    public resetSlderPosition() {
        if (this.isDestroyed) {
            return;
        }

        if (this.orientation === SliderOrientation.Vertical) {
            this.handleSprite.y = this.bounds.start;
        } else {
            this.handleSprite.x = this.bounds.start;
        }
    }

    public setBounds() {
        if (this.isDestroyed) {
            return;
        }

        if (this.orientation === SliderOrientation.Horizontal) {

            this.bounds = {
                start: this.track.x + this.handleSprite.width /2 + this.frameOffset,
                end: this.track.x + this.track.width - this.handleSprite.width /2 - this.frameOffset
            };
            this.distance = this.track.width - this.handleSprite.width - this.frameOffset;
        } else {
            this.bounds = {
                start: this.track.y + (this.track.height / 10),
                end: this.track.y + this.track.height - (this.track.height / 10)
            };
            this.distance = this.track.height - this.track.height / 10;
        }
        this.resetSlderPosition();
        this.update();
    }

    public getValue(): string {
        return this.value;
    }

    public setValue(value: number) {
        if (this.isDestroyed) {
            return;
        }

        this.setBounds();
        if (this.orientation === SliderOrientation.Horizontal) {
            this.handleSprite.x = this.reverse
                ? this.bounds.end - value * this.distance / this.maxValue
                : value * this.distance / this.maxValue + this.bounds.start;
        } else {
            // this.handleSprite.y = value*this.distance/this.maxValue + this.bounds.start;
            this.handleSprite.y = this.reverse
                ? this.bounds.end - value * this.distance / this.maxValue
                : value * this.distance / this.maxValue + this.bounds.start;
        }
        this.update();
        // return this.value;
    }

    public roundFunction(value: number): string {
        // override for different rounding
        return String(Math.floor(value));
    }

    public setupContentSlideSpeed(speedFraction: number) {
        if (this.isDestroyed) {
            return;
        }

        this.contentSlideSpeedModifier = speedFraction;
    }

    protected onMove(event) {
        if (this.isDestroyed) {
            return;
        }

        if (this.down && event.type === 'pointermove') {
            const diff = Layout.scalePoint(this.handleSprite.toLocal(event.data.global));
            this.applyDiff(diff);
            this.update();
        }

        if (event.type === 'wheel' && !GameModel.isMobile()) {
            const diff = Layout.scalePoint(new Point(event.wheelDeltaX * this.wheelRatio, -event.wheelDeltaY * this.wheelRatio));
            this.applyDiff(diff);
            this.update();
        }
    }
    protected onHitAreaMove(event) {
        if (this.isDestroyed) {
            return;
        }

        if (this.hitDown) {
            const currentPos = Layout.scalePoint(this.handleSprite.toLocal(event.data.global));
            const diff = new Point(this.hitDownPosition.x - currentPos.x, this.hitDownPosition.y - currentPos.y);
            if (this.orientation == SliderOrientation.Horizontal) {
                this.handleSprite.x += diff.x / (Layout.getWidth(this.hitArea) / Layout.canvasWidth) * this.contentSlideSpeedModifier;
                this.handleSprite.x = Math.max(this.bounds.start, this.handleSprite.x);
                this.handleSprite.x = Math.min(this.bounds.end, this.handleSprite.x);
            } else {
                this.handleSprite.y += diff.y / (Layout.getHeight(this.hitArea) / Layout.canvasHeight) * this.contentSlideSpeedModifier;
                this.handleSprite.y = Math.max(this.bounds.start, this.handleSprite.y);
                this.handleSprite.y = Math.min(this.bounds.end, this.handleSprite.y);
            }
            this.hitDownPosition = currentPos;
            this.update();
        }
    }

    protected onDown(event) {
        if (this.isDestroyed) {
            return;
        }

        this.down = true;

        document.addEventListener(PointerEvents.UP, () => {
            this.onUp();
        }, { once: true });
    }

    protected onHitDown(event) {
        if (this.isDestroyed) {
            return;
        }

        this.hitDown = true;
        this.hitDownPosition = Layout.scalePoint(this.handleSprite.toLocal(event.data.global));
        document.addEventListener(PointerEvents.UP, () => {
            this.onUp();
        }, { once: true });
    }

    protected onTrackDown(event) {
        if (this.isDestroyed) {
            return;
        }

        if (!Layout.landscape) {
            const diff = Layout.scalePoint(this.handleSprite.toLocal(event.data.global));
            this.applyDiff(diff);
        } else {
            if (this.orientation == SliderOrientation.Horizontal) {
                const newX = this.handle.toLocal(event.data.global).x;
                if ((newX - this.track.width - this.handleSprite.width) > this.track.width / 2) {
                    this.handleSprite.x = newX - this.handleSprite.width / 2;
                } else {
                    this.handleSprite.x = newX + this.handleSprite.width / 2;
                };
            } else {
                const newY = this.handle.toLocal(event.data.global).y;
                if (newY > this.track.height / 2) {
                    this.handleSprite.y = newY - this.handleSprite.height / 2;
                } else {
                    this.handleSprite.y = newY + this.handleSprite.height / 2;
                }
            }
        }

        this.update();
        this.down = true;
        document.addEventListener(PointerEvents.UP, () => {
            this.onUp();
        }, { once: true });
    }

    protected setUpListeners() {
        if (this.isDestroyed) {
            return;
        }

        this.handle.on(PointerEvents.MOVE, this.onMove, this);

        this.handle.on(PointerEvents.DOWN, this.onDown, this);

        this.track.interactive = true;

        this.track.on(PointerEvents.DOWN, this.onTrackDown, this);
        if (this.hitArea) {
            this.hitArea.interactive = true;
            this.hitArea.on(PointerEvents.DOWN, this.onHitDown, this);
            this.hitArea.on(PointerEvents.MOVE, this.onHitAreaMove, this)
        }
        this.wheelFunc = this.onMove.bind(this);
        App.instance.view.addEventListener("wheel", this.wheelFunc, false);
        if (this.upBtn) {
            this.upBtn.on(PointerEvents.DOWN, this.onClickUpBtn)
        }

        if (this.downBtn) {
            this.downBtn.on(PointerEvents.DOWN, this.onClickDownBtn)
        }

    }

    onClickUpBtn = () => {
        const newY = this.handleSprite.position.y - 10;
        if (newY > (this.track.y + this.handleSprite.height / 2)) {
            this.handleSprite.position.y = newY;
        } else {
            this.handleSprite.position.y = this.track.y + this.handleSprite.height / 2;
        }
        this.update();
    }
    onClickDownBtn = () => {
        const newY = this.handleSprite.position.y + 10;
        if (newY < (this.track.height + this.track.y - (this.handleSprite.height / 2))) {
            this.handleSprite.position.y = newY;
        } else {
            this.handleSprite.position.y = this.track.height + this.track.y - (this.handleSprite.height / 2);
        }
        this.update();
    }

    protected onUp() {
        if (this.isDestroyed) {
            return;
        }

        this.down = false;
        this.hitDown = false;
        if (this.upSound) {
            SoundController.playSound(this.upSound);
        }
    }

    protected update() {
        if (this.isDestroyed) {
            return;
        }

        if (this.orientation === SliderOrientation.Horizontal) {
            this.value = this.reverse
                ? this.roundFunction(this.maxValue * (this.bounds.end - this.handleSprite.x) / (this.distance + this.frameOffset))
                : this.roundFunction(this.maxValue * (this.handleSprite.x - this.bounds.start) / (this.distance - this.frameOffset));
        } else {
            this.value = this.reverse
                ? this.roundFunction(this.maxValue * (this.bounds.end - this.handleSprite.y) / this.distance)
                : this.roundFunction(this.maxValue * (this.handleSprite.y - this.bounds.start) / this.distance);
        }

        if (this.handle.text) {
            this.handle.text.text = this.value;
        }
        if (this.fillMask) {
            if (this.orientation === SliderOrientation.Horizontal) {
                this.fillMask.width = this.reverse ? this.bounds.end - this.handleSprite.x + this.handleSprite.width /2 : this.handleSprite.x - this.bounds.start + this.handleSprite.width /2;
                if (this.reverse) {
                    this.fillMask.x = this.handleSprite.x;
                }
            } else {
                this.fillMask.height = this.reverse ? this.bounds.end - this.handleSprite.y : this.handleSprite.y - this.bounds.start;
                if (this.reverse) {
                    this.fillMask.y = this.handleSprite.y;
                }
            }
        }
        this.emit(PointerEvents.UPDATE);
    }

    protected onOrientationChanged() {
        if (this.isDestroyed) {
            return;
        }

        // Keeping the current value, because setBounds will reset the slider.
        const oldValue = parseFloat(this.getValue());
        this.setBounds();

        // Set back the old value.
        this.setValue(oldValue);

        // Update the fill
        if (this.fill && this.fillMask) {
            this.fill.parent.removeChild(this.fillMask);
        }

        this.createFill();
    }

    protected applyDiff(diff: Point) {

        const coef = this.hitArea ? 0.5 : 0;
        const handleOffsetX = this.handleSprite.width * coef + this.handleSprite.width /2 + this.frameOffset;
        const handleOffsetY = this.handleSprite.height * coef;
        if (this.orientation === SliderOrientation.Horizontal) {
            let newX = this.handleSprite.x + diff.x;
            newX = Math.max(newX, this.track.x + handleOffsetX);
            newX = Math.min(newX, this.track.x + this.track.width - handleOffsetX);
            this.handleSprite.x = newX;
        } else {
            let newY = this.handleSprite.y + diff.y;
            newY = Math.max(newY, this.track.y + handleOffsetY);
            newY = Math.min(newY, this.track.y + this.track.height - handleOffsetY);
            this.handleSprite.y = newY;
        }
    }

    protected createFill() {
        if (this.fill) {
            this.fillMask = new Graphics();
            this.fillMask.beginFill(0x000000, 0.01);
            this.fillMask.drawRect(0, 0, this.fill.height, this.fill.height);
            if (this.orientation === SliderOrientation.Horizontal) {
                this.fillMask.width = this.handleSprite.x - this.bounds.start;
                this.fillMask.height = this.fill.height;
            } else {
                this.fillMask.width = this.fill.width;
                this.fillMask.height = this.handleSprite.y / this.bounds.start;
            }

            this.fillMask.position.set(this.fill.x, this.fill.y);
            this.fill.mask = this.fillMask;
            this.fillMask.name = "slider_mask";
            this.fill.parent.addChild(this.fillMask);
        }
    }
}

interface ISliderElements {
    handle: Button,
    upBtn?: Button,
    downBtn?: Button,
    track: Sprite,
    fill?: Sprite,
    hitArea?: Graphics,
    maxText?: Text | BitmapText | TextField,
    minText?: Text | BitmapText | TextField,
}

export enum SliderOrientation {
    Vertical = "Vertical",
    Horizontal = "Horizontal"
}
